import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { CreditCard, Loader2 } from "lucide-react";
import { API } from "../config";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { Toast } from "../components/Toast";
import GiftCardPromoBanner from "../components/GiftCardPromoBanner";

export default function CheckoutPage() {
  const { cart, fetchCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("stripe");
  const [toast, setToast] = useState(null);
  const [form, setForm] = useState({
    shipping_address: "",
    shipping_city: "",
    shipping_postcode: "",
    phone: ""
  });

  if (!user || !cart?.items?.length) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-12 text-center">
        <p className="text-gray-500">Your cart is empty</p>
        <Link to="/products" className="text-[#00a99d] hover:underline">Continue shopping</Link>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/checkout/create-order`, {
        ...form,
        origin_url: window.location.origin,
        payment_method: paymentMethod
      });

      if (res.data.payment_method === "stripe" && res.data.url) {
        window.location.href = res.data.url;
      } else {
        const { order_id, total: orderTotal, payment_method: pm } = res.data;
        navigate(`/payment-success?order_id=${order_id}&amount=${orderTotal.toFixed(2)}&method=${pm}`);
      }
    } catch (err) {
      setToast({ message: err.response?.data?.detail || "Failed to create order", type: "error" });
      setLoading(false);
    }
  };

  const total = (() => {
    const sub = cart.total;
    const del = sub >= 30 ? 0 : 4.99;
    const vat = +((sub + del) * 0.20).toFixed(2);
    return { sub, del, vat, total: +((sub + del + vat)).toFixed(2) };
  })();

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-gray-800 mb-8">Checkout</h1>

      <GiftCardPromoBanner />

      <div className="grid lg:grid-cols-2 gap-8">
        <form onSubmit={handleSubmit} className="card p-6" data-testid="checkout-form">
          <h3 className="text-lg font-semibold mb-4">Customer Details</h3>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium mb-1">Full name</label>
              <input
                type="text"
                className="form-input"
                value={user?.name || ""}
                readOnly
                data-testid="customer-name-readonly"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Email</label>
              <input
                type="email"
                className="form-input"
                value={user?.email || ""}
                readOnly
                data-testid="customer-email-readonly"
              />
            </div>
          </div>

          <h3 className="text-lg font-semibold mb-4">Delivery Address</h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Address</label>
              <input
                type="text"
                required
                className="form-input"
                value={form.shipping_address}
                onChange={(e) => setForm({ ...form, shipping_address: e.target.value })}
                placeholder="123 Main Street"
                data-testid="address-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">City</label>
                <input
                  type="text"
                  required
                  className="form-input"
                  value={form.shipping_city}
                  onChange={(e) => setForm({ ...form, shipping_city: e.target.value })}
                  placeholder="Manchester"
                  data-testid="city-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Postcode</label>
                <input
                  type="text"
                  required
                  className="form-input"
                  value={form.shipping_postcode}
                  onChange={(e) => setForm({ ...form, shipping_postcode: e.target.value })}
                  placeholder="M1 1AA"
                  data-testid="postcode-input"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Phone</label>
              <input
                type="tel"
                required
                className="form-input"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="07123 456789"
                data-testid="phone-input"
              />
            </div>
          </div>

          <div className="mt-6">
            <h3 className="text-lg font-semibold mb-3">Payment Method</h3>
            <div className="space-y-3">
              <label
                className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${paymentMethod === "stripe" ? "border-[#00a99d] bg-teal-50" : "border-gray-200 hover:border-gray-300"}`}
                data-testid="stripe-payment-option"
              >
                <input
                  type="radio"
                  name="payment"
                  value="stripe"
                  checked={paymentMethod === "stripe"}
                  onChange={() => setPaymentMethod("stripe")}
                  className="accent-[#00a99d]"
                />
                <CreditCard className="w-5 h-5 text-[#635bff]" />
                <div className="flex-1">
                  <span className="font-medium">Pay by Card</span>
                  <span className="ml-2 text-xs bg-[#00a99d] text-white px-2 py-0.5 rounded-full">Recommended</span>
                  <p className="text-xs text-gray-500 mt-0.5">Secure payment via Stripe. Visa, Mastercard, Amex accepted.</p>
                </div>
              </label>
              <label
                className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${paymentMethod === "sumup" ? "border-[#00a99d] bg-teal-50" : "border-gray-200 hover:border-gray-300"}`}
                data-testid="sumup-payment-option"
              >
                <input
                  type="radio"
                  name="payment"
                  value="sumup"
                  checked={paymentMethod === "sumup"}
                  onChange={() => setPaymentMethod("sumup")}
                  className="accent-[#00a99d]"
                />
                <CreditCard className="w-5 h-5 text-[#1a73e8]" />
                <div className="flex-1">
                  <span className="font-medium">Pay via SumUp</span>
                  <p className="text-xs text-gray-500 mt-0.5">Alternative payment. You'll be redirected to SumUp to complete payment.</p>
                </div>
              </label>
              <label
                className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${paymentMethod === "credit_30day" ? "border-[#00a99d] bg-teal-50" : "border-gray-200 hover:border-gray-300"}`}
                data-testid="credit-payment-option"
              >
                <input
                  type="radio"
                  name="payment"
                  value="credit_30day"
                  checked={paymentMethod === "credit_30day"}
                  onChange={() => setPaymentMethod("credit_30day")}
                  className="accent-[#00a99d]"
                />
                <CreditCard className="w-5 h-5 text-[#00a99d]" />
                <div className="flex-1">
                  <span className="font-medium">30-Day Credit Account</span>
                  <span className="ml-2 text-[10px] font-semibold bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full uppercase tracking-wide">Trade only</span>
                  <p className="text-xs text-gray-500 mt-0.5">Order now &amp; pay within 30 days by BACS. Subject to credit approval &mdash; we'll email your invoice immediately.</p>
                </div>
              </label>
            </div>
          </div>

          <button type="submit" disabled={loading} className="btn-primary w-full mt-6" data-testid="place-order-btn">
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 className="w-5 h-5 animate-spin" />
                Processing order...
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <CreditCard className="w-5 h-5" />
                {paymentMethod === "stripe"
                  ? `Pay \u00A3${total.total.toFixed(2)} with Card`
                  : paymentMethod === "credit_30day"
                  ? `Place order on 30-day credit \u2014 \u00A3${total.total.toFixed(2)}`
                  : `Place Order \u2014 \u00A3${total.total.toFixed(2)}`}
              </span>
            )}
          </button>
        </form>

        <div className="card p-6 h-fit">
          <h3 className="text-lg font-semibold mb-4">Order Summary</h3>
          <div className="space-y-3">
            {cart.items.map((item) => (
              <div key={item.product_id} className="flex justify-between text-sm">
                <span>{item.name} x {item.quantity}</span>
                <span>&pound;{(item.price * item.quantity).toFixed(2)}</span>
              </div>
            ))}
          </div>
          <div className="border-t mt-4 pt-4 space-y-2">
            <div className="flex justify-between text-sm" data-testid="summary-subtotal">
              <span>Subtotal (excl. VAT)</span>
              <span>&pound;{total.sub.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span>Delivery</span>
              <span className={total.del === 0 ? "text-green-600" : ""}>{total.del === 0 ? "FREE" : `\u00A3${total.del.toFixed(2)}`}</span>
            </div>
            <div className="flex justify-between text-sm" data-testid="summary-vat">
              <span>VAT (20%)</span>
              <span>&pound;{total.vat.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-lg font-semibold pt-2 border-t">
              <span>Total</span>
              <span data-testid="summary-total">&pound;{total.total.toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}
